import { Badge } from '@/components/ui/badge';
import {
  Activity,
  MessageSquare,
  Paperclip,
  Tag,
  Calendar,
  AlertCircle,
  CheckCircle2,
  Clock,
  Plus,
  Edit,
  Link2,
  X,
  UserPlus,
  Trash2,
} from 'lucide-react';
import type { TaskActivity } from '@/types/task';

interface TaskDetailActivityProps {
  taskId: string;
  activity: TaskActivity[];
}

export function TaskDetailActivity({ taskId, activity }: Readonly<TaskDetailActivityProps>) {
  console.log('TaskDetailActivity render - taskId:', taskId, 'activity count:', activity?.length, 'activity:', activity);
  
  // Helper function to format action text to be user-friendly
  const formatActionText = (text: string): string => {
    if (!text) return '';
    
    // Add space before capital letters (camelCase/PascalCase to Title Case)
    // e.g., "LinkAdded" -> "Link Added", "CommentAdded" -> "Comment Added"
    return text
      .replace(/([A-Z])/g, ' $1') // Add space before capitals
      .trim() // Remove leading space
      .replace(/\s+/g, ' '); // Normalize multiple spaces to single space
  };
  
  const getActivityIcon = (type: string, description: string) => {
    const lowerType = type?.toLowerCase() ?? '';
    const lowerDesc = description?.toLowerCase() ?? '';
    
    // Created
    if (lowerType.includes('created') || lowerDesc.includes('created')) {
      return <Plus className="h-4 w-4" />;
    }
    
    // Updated/Changed
    if (lowerType.includes('updated') || lowerDesc.includes('updated')) {
      return <Edit className="h-4 w-4" />;
    }
    
    // Status change
    if (type === 'status-change' || lowerDesc.includes('status')) {
      return <AlertCircle className="h-4 w-4" />;
    }
    
    // Assignee
    if (type === 'assignee-change' || lowerDesc.includes('assigned')) {
      return <UserPlus className="h-4 w-4" />;
    }
    
    // Comments
    if (type === 'comment' || lowerDesc.includes('comment')) {
      return <MessageSquare className="h-4 w-4" />;
    }
    
    // Attachments
    if (type === 'attachment' || lowerDesc.includes('attachment')) {
      return <Paperclip className="h-4 w-4" />;
    }
    
    // Tags
    if (type === 'tag-removed' || (lowerDesc.includes('tag') && lowerDesc.includes('removed'))) {
      return <X className="h-4 w-4" />;
    }
    if (type === 'tag-added' || lowerDesc.includes('tag')) {
      return <Tag className="h-4 w-4" />;
    }
    
    // Links
    if (type === 'link-removed' || (lowerDesc.includes('link') && lowerDesc.includes('removed'))) {
      return <Trash2 className="h-4 w-4" />;
    }
    if (type === 'link-added' || lowerDesc.includes('link')) {
      return <Link2 className="h-4 w-4" />;
    }
    
    // Due date
    if (type === 'due-date-change' || lowerDesc.includes('due date')) {
      return <Calendar className="h-4 w-4" />;
    }
    
    // Checklist
    if (type === 'checklist-completed') {
      return <CheckCircle2 className="h-4 w-4" />;
    }
    
    // Time logged
    if (type === 'time-logged') {
      return <Clock className="h-4 w-4" />;
    }
    
    // Default
    return <Activity className="h-4 w-4" />;
  };

  const getActivityColor = (type: string, description: string) => {
    const lowerType = type?.toLowerCase() ?? '';
    const lowerDesc = description?.toLowerCase() ?? '';
    
    // Created
    if (lowerType.includes('created') || lowerDesc.includes('created')) {
      return { icon: 'text-emerald-600 dark:text-emerald-400', border: 'border-emerald-200/50 dark:border-emerald-800/30' };
    }
    
    // Updated
    if (lowerType.includes('updated') || lowerDesc.includes('updated')) {
      return { icon: 'text-blue-600 dark:text-blue-400', border: 'border-blue-200/50 dark:border-blue-800/30' };
    }
    
    // Status change
    if (type === 'status-change' || lowerDesc.includes('status')) {
      return { icon: 'text-cyan-600 dark:text-cyan-400', border: 'border-cyan-200/50 dark:border-cyan-800/30' };
    }
    
    // Assignee
    if (type === 'assignee-change' || lowerDesc.includes('assigned')) {
      return { icon: 'text-purple-600 dark:text-purple-400', border: 'border-purple-200/50 dark:border-purple-800/30' };
    }
    
    // Comments
    if (type === 'comment' || lowerDesc.includes('comment')) {
      return { icon: 'text-green-600 dark:text-green-400', border: 'border-green-200/50 dark:border-green-800/30' };
    }
    
    // Attachments
    if (type === 'attachment' || lowerDesc.includes('attachment')) {
      return { icon: 'text-orange-600 dark:text-orange-400', border: 'border-orange-200/50 dark:border-orange-800/30' };
    }
    
    // Tags removed
    if (type === 'tag-removed' || (lowerDesc.includes('tag') && lowerDesc.includes('removed'))) {
      return { icon: 'text-red-600 dark:text-red-400', border: 'border-red-200/50 dark:border-red-800/30' };
    }
    
    // Tags added
    if (type === 'tag-added' || lowerDesc.includes('tag')) {
      return { icon: 'text-pink-600 dark:text-pink-400', border: 'border-pink-200/50 dark:border-pink-800/30' };
    }
    
    // Links removed
    if (type === 'link-removed' || (lowerDesc.includes('link') && lowerDesc.includes('removed'))) {
      return { icon: 'text-red-600 dark:text-red-400', border: 'border-red-200/50 dark:border-red-800/30' };
    }
    
    // Links added
    if (type === 'link-added' || lowerDesc.includes('link')) {
      return { icon: 'text-indigo-600 dark:text-indigo-400', border: 'border-indigo-200/50 dark:border-indigo-800/30' };
    }
    
    // Due date
    if (type === 'due-date-change' || lowerDesc.includes('due date')) {
      return { icon: 'text-amber-600 dark:text-amber-400', border: 'border-amber-200/50 dark:border-amber-800/30' };
    }
    
    // Checklist
    if (type === 'checklist-completed') {
      return { icon: 'text-teal-600 dark:text-teal-400', border: 'border-teal-200/50 dark:border-teal-800/30' };
    }
    
    // Time logged
    if (type === 'time-logged') {
      return { icon: 'text-violet-600 dark:text-violet-400', border: 'border-violet-200/50 dark:border-violet-800/30' };
    }
    
    // Default
    return { icon: 'text-gray-600 dark:text-gray-400', border: 'border-gray-200/50 dark:border-gray-800/30' };
  };

  if (!activity || !Array.isArray(activity) || activity.length === 0) {
    return (
      <div className="text-center py-8">
        <Activity className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
        <p className="text-xs text-muted-foreground">
          No activity yet
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {activity.map((item, index) => {
        // Debug logging
        console.log('Activity item:', item);
        
        const colors = getActivityColor(item.type, item.description);
        
        return (
          <div 
            key={item.id} 
            className={`p-3 rounded-md border bg-white dark:bg-black ${colors.border} transition-all hover:border-opacity-60`}
          >
            <div className="flex gap-2.5 items-start">
              <div className={`mt-0.5 ${colors.icon} flex-shrink-0`}>
                {getActivityIcon(item.type, item.description)}
              </div>
              <div className="flex-1 min-w-0 space-y-0.5">
                {/* Action as main heading */}
                <div className="text-sm font-semibold text-foreground leading-tight">
                  {formatActionText(item.description?.trim() || 'Activity')}
                </div>
                
                {/* User and date on same line */}
                <div className="text-xs text-muted-foreground flex items-center gap-1.5">
                  <span className="font-medium text-foreground/70">{item.createdByName?.trim() || 'Unknown User'}</span>
                  <span className="text-muted-foreground/50">•</span>
                  <span className="text-muted-foreground/70">
                    {(() => {
                      try {
                        const date = new Date(item.createdAt);
                        
                        // Check if date is valid
                        if (isNaN(date.getTime())) {
                          return 'Recently';
                        }
                        
                        const now = new Date();
                        const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
                        
                        if (diffInHours < 1) {
                          return 'Just now';
                        } else if (diffInHours < 24) {
                          return date.toLocaleTimeString('en-US', { 
                            hour: 'numeric', 
                            minute: '2-digit',
                            hour12: true 
                          });
                        } else if (diffInHours < 48) {
                          return 'Yesterday at ' + date.toLocaleTimeString('en-US', { 
                            hour: 'numeric', 
                            minute: '2-digit',
                            hour12: true 
                          });
                        } else {
                          return date.toLocaleDateString('en-US', { 
                            month: 'short', 
                            day: 'numeric',
                            year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
                          });
                        }
                      } catch (error) {
                        console.error('Error formatting date:', error);
                        return 'Recently';
                      }
                    })()}
                  </span>
                </div>
                
                {/* Show old/new values for field changes */}
                {item.oldValue && item.newValue && (
                  <div className="mt-1.5 flex items-center gap-1.5 text-xs">
                    <Badge variant="secondary" className="font-mono text-xs px-1.5 py-0">
                      {item.oldValue}
                    </Badge>
                    <span className="text-muted-foreground/60">→</span>
                    <Badge variant="secondary" className="font-mono text-xs px-1.5 py-0">
                      {item.newValue}
                    </Badge>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
